import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';

const AdminQuests = () => {
  const [quests, setQuests] = useState([]);
  const [currentQuestIndex, setCurrentQuestIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllWeeks, setShowAllWeeks] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedWeekForEmail, setSelectedWeekForEmail] = useState(0);

  useEffect(() => {
    fetchQuests();
  }, []);

  // Add new effect to set current week when quests are loaded
  useEffect(() => {
    const currentQuest = quests[currentQuestIndex]?.quest;
    if (currentQuest?.quest_plan) {
      const currentDate = new Date();
      const currentWeekIndex = currentQuest.quest_plan.findIndex((week) => {
        const weekStart = new Date(week.week_start);
        const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
        return currentDate >= weekStart && currentDate < weekEnd;
      });
      if (currentWeekIndex !== -1) {
        setSelectedWeekForEmail(currentWeekIndex);
      }
    }
  }, [quests, currentQuestIndex]);

  const fetchQuests = async () => {
    try {
      setLoading(true);
      // Get all quests from all schools
      const response = await axios.get('/api/v1/quest/all');
      setQuests(response.data.quests || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch quests');
      console.error('Error fetching quests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestIndex((prev) => (prev > 0 ? prev - 1 : quests.length - 1));
  };

  const handleNext = () => {
    setCurrentQuestIndex((prev) => (prev < quests.length - 1 ? prev + 1 : 0));
  };

  const handleQuestSelect = (event) => {
    const selectedIndex = quests.findIndex(
      (q) => q.quest.quest_id === parseInt(event.target.value)
    );
    if (selectedIndex !== -1) {
      setCurrentQuestIndex(selectedIndex);
    }
  };

  const getVisibleWeeks = (questPlan) => {
    if (showAllWeeks) return questPlan;

    const currentDate = new Date();
    const currentWeekIndex = questPlan.findIndex((week) => {
      const weekStart = new Date(week.week_start);
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      return currentDate >= weekStart && currentDate < weekEnd;
    });

    if (currentWeekIndex === -1) return questPlan;

    const visibleWeeks = [];
    // Add previous week if it exists
    if (currentWeekIndex > 0) {
      visibleWeeks.push(questPlan[currentWeekIndex - 1]);
    }
    // Add current week
    visibleWeeks.push(questPlan[currentWeekIndex]);
    // Add next week if it exists
    if (currentWeekIndex < questPlan.length - 1) {
      visibleWeeks.push(questPlan[currentWeekIndex + 1]);
    }

    return visibleWeeks;
  };

  const renderTaskList = (tasks, type) => {
    if (!tasks || tasks.length === 0) return <p>No {type} tasks</p>;

    return (
      <ul className="list-disc pl-5">
        {tasks.map((task, index) => (
          <li key={index} className="mb-2">
            {type === 'leetcode' && (
              <span className={task.completed ? 'line-through text-gray-500' : ''}>
                {task.problem_name}
              </span>
            )}
            {type === 'apps' && (
              <span className={task.completed ? 'line-through text-gray-500' : ''}>
                {task.company_name}
              </span>
            )}
            {type === 'lessons' && (
              <span className={task.completed ? 'line-through text-gray-500' : ''}>
                {task.lesson_name}
              </span>
            )}
            {type === 'resume' && (
              <span className={task.completed ? 'line-through text-gray-500' : ''}>
                {task.task_name}
              </span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const generateEmailContent = (weekIndex) => {
    if (!currentQuest || !currentUser) return '';

    const week = currentQuest.quest_plan[weekIndex];
    const prevWeek = weekIndex > 0 ? currentQuest.quest_plan[weekIndex - 1] : null;

    // Count completed tasks from previous week
    let completedTasks = 0;
    let totalTasks = 0;
    if (prevWeek) {
      const tasks = prevWeek.tasks;
      Object.values(tasks).forEach((taskList) => {
        if (Array.isArray(taskList)) {
          totalTasks += taskList.length;
          completedTasks += taskList.filter((task) => task.completed).length;
        }
      });
    }

    // Count current week's tasks
    const currentTasks = week.tasks;
    const leetcodeCount = currentTasks.leetcode?.length || 0;
    const appsCount = currentTasks.apps?.length || 0;

    const completionRate = totalTasks > 0 ? completedTasks / totalTasks : 0;
    const isGoodWeek = completionRate > 0.5;

    return `Hi ${currentUser.first_name},

Happy week ${weekIndex + 1} of your InternHub Quest!

Last week, you completed ${completedTasks}/${totalTasks} tasks! ${isGoodWeek ? 'Amazing job - you killed it!' : "Tough week - but that's okay!"}

Here's what you have to tackle this week, starting today:
${currentTasks.lessons?.length ? '- Finish all the lessons on InternHub\n' : ''}
${currentTasks.resume?.length ? '- Draft, revise, and finalize your resume\n' : ''}
${leetcodeCount ? `- Finish ${leetcodeCount} Leetcode questions\n` : ''}
${appsCount ? `- Apply to ${appsCount} job${appsCount > 1 ? 's' : ''}\n` : ''}

Keep killing it at https://internhubapp.com! ${!isGoodWeek ? 'You can adjust your goals anytime by replying to this email.' : ''}

-Serena

(reply 'unsubscribe' to unsubscribe)`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  const currentQuest = quests[currentQuestIndex]?.quest;
  const currentUser = quests[currentQuestIndex]?.user;

  return (
    <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-blue-600">Quests</h3>
        <div className="flex items-center space-x-4">
          <select
            onChange={handleQuestSelect}
            value={currentQuest?.quest_id || ''}
            className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Select a quest...</option>
            {quests.map((q) => (
              <option key={q.quest.quest_id} value={q.quest.quest_id}>
                {q.user.first_name} {q.user.last_name} - {q.quest.grad_year}
              </option>
            ))}
          </select>
          <div className="flex items-center space-x-2">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={showAllWeeks}
                onChange={() => setShowAllWeeks(!showAllWeeks)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                {showAllWeeks ? 'All Weeks' : 'Current Week'}
              </span>
            </label>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handlePrevious}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              Previous
            </button>
            <button
              onClick={handleNext}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {currentQuest ? (
        <div className="space-y-6">
          {/* User Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold mb-2">User Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">
                  {currentUser.first_name} {currentUser.last_name}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{currentUser.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Graduation Year</p>
                <p className="font-medium">{currentQuest.grad_year}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Hours per Week</p>
                <p className="font-medium">{currentQuest.hours_per_week}</p>
              </div>
            </div>
          </div>

          {/* Generate Email Button */}
          <div className="flex items-center space-x-4">
            <select
              value={selectedWeekForEmail}
              onChange={(e) => setSelectedWeekForEmail(parseInt(e.target.value))}
              className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {currentQuest.quest_plan.map((week, index) => {
                const isCurrentWeek =
                  new Date(week.week_start) <= new Date() &&
                  new Date(week.week_start).getTime() + 7 * 24 * 60 * 60 * 1000 > new Date();
                return (
                  <option key={index} value={index}>
                    Week {index + 1} - {new Date(week.week_start).toLocaleDateString()}
                    {isCurrentWeek ? ' (Current Week)' : ''}
                  </option>
                );
              })}
            </select>
            <button
              onClick={() => setShowEmailModal(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            >
              Generate Email
            </button>
          </div>

          {/* Quest Plan */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quest Plan</h4>
            <div className="space-y-4">
              {getVisibleWeeks(currentQuest.quest_plan).map((week, weekIndex) => {
                const isCurrentWeek =
                  new Date(week.week_start) <= new Date() &&
                  new Date(week.week_start).getTime() + 7 * 24 * 60 * 60 * 1000 > new Date();

                return (
                  <div
                    key={weekIndex}
                    className={`border rounded-lg p-4 ${
                      isCurrentWeek ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <h5 className="font-medium mb-2">
                      Week {weekIndex + 1} - {new Date(week.week_start).toLocaleDateString()}
                      {isCurrentWeek && (
                        <span className="ml-2 text-sm text-blue-600 font-normal">
                          (Current Week)
                        </span>
                      )}
                    </h5>
                    <div className="space-y-2">
                      {week.tasks.leetcode && week.tasks.leetcode.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            LeetCode Problems (
                            {week.tasks.leetcode.filter((p) => p.completed).length}/
                            {week.tasks.leetcode.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {week.tasks.leetcode.map((problem, index) => (
                              <span
                                key={index}
                                className={`${problem.completed ? 'text-green-600' : 'text-red-600'} ${
                                  index < week.tasks.leetcode.length - 1
                                    ? "after:content-[',']"
                                    : ''
                                }`}
                              >
                                {problem.completed ? problem.problem_name : 'Missing Problem'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {week.tasks.apps && week.tasks.apps.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            Applications ({week.tasks.apps.filter((a) => a.completed).length}/
                            {week.tasks.apps.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {week.tasks.apps.map((app, index) => (
                              <span
                                key={index}
                                className={`${app.completed ? 'text-green-600' : 'text-red-600'} ${
                                  index < week.tasks.apps.length - 1 ? "after:content-[',']" : ''
                                }`}
                              >
                                {app.completed ? app.company_name : 'Missing Application'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {week.tasks.lessons && week.tasks.lessons.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-600">Lessons</p>
                          <ul className="list-disc list-inside">
                            {week.tasks.lessons.map((lesson, index) => (
                              <li key={index} className="flex items-center">
                                <span
                                  className={lesson.completed ? 'text-green-600' : 'text-red-600'}
                                >
                                  {lesson.lesson_name}
                                </span>
                                {lesson.completed && <span className="ml-2 text-green-600">✓</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {week.tasks.resume && week.tasks.resume.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-600">Resume Tasks</p>
                          <ul className="list-disc list-inside">
                            {week.tasks.resume.map((task, index) => (
                              <li key={index} className="flex items-center">
                                <span
                                  className={task.completed ? 'text-green-600' : 'text-red-600'}
                                >
                                  {task.task_name}
                                </span>
                                {task.completed && <span className="ml-2 text-green-600">✓</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">No quests found</div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-blue-600">Generated Email</h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap font-mono text-sm">
              {generateEmailContent(selectedWeekForEmail)}
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(generateEmailContent(selectedWeekForEmail));
                  alert('Email copied to clipboard!');
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              >
                Copy to Clipboard
              </button>
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuests;
